import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import Overline from "@/components/Overline";
import { PRODUCT_CATEGORIES } from "@/data/site";
import { useQuoteModal } from "@/context/QuoteModalContext";

export default function Products() {
  const { openModal } = useQuoteModal();
  return (
    <div data-testid="products-page" className="border-b border-slate-200">
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="tech-grid tech-grid-fade absolute inset-0 opacity-50" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-28">
          <Overline>Products</Overline>
          <h1 className="font-heading mt-6 max-w-3xl text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            A complete catalogue for modern enterprise IT.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-700 sm:text-lg">
            From individual semiconductors to fully configured server racks —
            sourced from authorised channels and trusted manufacturers, ready
            to ship from Dubai.
          </p>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-24">
          <div className="space-y-6">
            {PRODUCT_CATEGORIES.map((cat, idx) => (
              <article
                key={cat.slug}
                data-testid={`product-category-${cat.slug}`}
                className="group grid items-stretch gap-0 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 lg:grid-cols-12"
              >
                <div className={`relative lg:col-span-5 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="relative h-64 w-full lg:h-full">
                    <img
                      src={cat.image}
                      alt={cat.title}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-white via-white/50 to-transparent lg:bg-gradient-to-r" />
                  </div>
                </div>
                <div className={`flex flex-col gap-5 p-8 lg:col-span-7 lg:p-12 ${idx % 2 === 1 ? "lg:order-1" : ""}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-cyan-600">
                      / Category · {String(idx + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h2 className="font-heading text-2xl font-medium text-slate-900 sm:text-3xl">
                    {cat.title}
                  </h2>
                  <p className="max-w-xl text-sm leading-relaxed text-slate-500 sm:text-base">
                    {cat.summary}
                  </p>
                  <ul className="grid grid-cols-1 gap-2 pt-2 sm:grid-cols-2">
                    {cat.items.map((it) => (
                      <li
                        key={it}
                        className="flex items-center gap-2 text-sm text-slate-700"
                      >
                        <Check className="h-4 w-4 text-cyan-600" />
                        {it}
                      </li>
                    ))}
                  </ul>
                  <div className="pt-3">
                    <Button
                      variant="outline"
                      data-testid={`product-cta-${cat.slug}-btn`}
                      onClick={() => openModal({ category: cat.title })}
                      className="rounded-md border-slate-300 bg-transparent text-slate-900 hover:border-cyan-600 hover:bg-transparent hover:text-cyan-700"
                    >
                      Request quote
                      <ArrowUpRight className="ml-1.5 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
